import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import {
  makeStyles,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Chip,
  Tooltip,
  Fade,
  useTheme,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";

// Icons
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import TextFieldsIcon from "@material-ui/icons/TextFields";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];
    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }
    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);
    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    minHeight: "calc(100vh - 70px)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  // Header
  header: {
    marginBottom: theme.spacing(3),
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  // Add Button
  addButton: {
    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    color: "#fff",
    borderRadius: 10,
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
      boxShadow: "0 6px 20px rgba(16, 185, 129, 0.5)",
    },
  },
  // Search
  searchWrapper: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  searchField: {
    minWidth: 300,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
      },
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  // Stats
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(2, 3),
    borderRadius: 12,
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 150,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  // Table
  tableContainer: {
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  tableWrapper: {
    overflowX: "auto",
    maxHeight: "calc(100vh - 400px)",
    ...theme.scrollbarStyles,
  },
  table: {
    "& .MuiTableHead-root": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      "& .MuiTableCell-head": {
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        padding: theme.spacing(2),
        whiteSpace: "nowrap",
      },
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
      "& .MuiTableCell-body": {
        padding: theme.spacing(1.5, 2),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
      },
    },
  },
  // Shortcode chip
  shortcodeChip: {
    fontFamily: "monospace",
    fontWeight: 700,
    fontSize: "0.85rem",
    backgroundColor: alpha("#10B981", 0.15),
    color: "#059669",
    borderRadius: 8,
  },
  // Media chip
  mediaChip: {
    borderRadius: 8,
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  hasMedia: {
    backgroundColor: alpha("#3B82F6", 0.15),
    color: "#2563EB",
  },
  noMedia: {
    backgroundColor: alpha(theme.palette.text.secondary, 0.1),
    color: theme.palette.text.secondary,
  },
  // Action Buttons
  actionBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
  deleteBtn: {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.2),
    },
  },
  // Empty state
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    background: alpha("#10B981", 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    color: "#10B981",
  },
}));

const QuickMessages = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);

  const socketManager = useContext(SocketContext);

  // Stats
  const totalMessages = quickemessages.length;
  const withMedia = quickemessages.filter((q) => q.mediaName).length;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company${companyId}-quickemessage`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager, user.companyId]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber, userId: user.id },
      });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <Box className={classes.root}>
      {/* Modals */}
      <ConfirmationModal
        title={
          deletingQuickemessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />

      {/* Header */}
      <Fade in timeout={400}>
        <Box className={classes.header}>
          <Box className={classes.headerTop}>
            <Box className={classes.titleSection}>
              <Box className={classes.titleIcon}>
                <FlashOnIcon />
              </Box>
              <Box>
                <Typography className={classes.title}>
                  {i18n.t("quickMessages.title")}
                </Typography>
                <Typography className={classes.subtitle}>
                  Crie atalhos para mensagens frequentes
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              className={classes.addButton}
              startIcon={<AddIcon />}
              onClick={handleOpenQuickMessageDialog}
            >
              {i18n.t("quickMessages.buttons.add")}
            </Button>
          </Box>

          {/* Search */}
          <Box className={classes.searchWrapper}>
            <TextField
              placeholder={i18n.t("quickMessages.searchPlaceholder")}
              variant="outlined"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Stats */}
          <Box className={classes.statsRow} style={{ marginTop: 24 }}>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha("#10B981", 0.15) }}
              >
                <FlashOnIcon style={{ color: "#10B981" }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{totalMessages}</Typography>
                <Typography className={classes.statLabel}>Total</Typography>
              </Box>
            </Box>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha("#3B82F6", 0.15) }}
              >
                <AttachFileIcon style={{ color: "#3B82F6" }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>{withMedia}</Typography>
                <Typography className={classes.statLabel}>Com Mídia</Typography>
              </Box>
            </Box>
            <Box className={classes.statCard}>
              <Box
                className={classes.statIcon}
                style={{ background: alpha(theme.palette.text.secondary, 0.15) }}
              >
                <TextFieldsIcon style={{ color: theme.palette.text.secondary }} />
              </Box>
              <Box>
                <Typography className={classes.statValue}>
                  {totalMessages - withMedia}
                </Typography>
                <Typography className={classes.statLabel}>Só Texto</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Table */}
      <Fade in timeout={600}>
        <Paper className={classes.tableContainer} elevation={0}>
          <Box className={classes.tableWrapper} onScroll={handleScroll}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("quickMessages.table.shortcode")}</TableCell>
                  <TableCell align="center">{i18n.t("quickMessages.table.mediaName")}</TableCell>
                  <TableCell align="center">{i18n.t("quickMessages.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quickemessages.map((quickemessage) => (
                  <TableRow key={quickemessage.id}>
                    <TableCell>
                      <Chip
                        label={quickemessage.shortcode}
                        size="small"
                        className={classes.shortcodeChip}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={quickemessage.mediaName ? <AttachFileIcon /> : null}
                        label={quickemessage.mediaName || i18n.t("quickMessages.noAttachment")}
                        size="small"
                        className={`${classes.mediaChip} ${
                          quickemessage.mediaName ? classes.hasMedia : classes.noMedia
                        }`}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.editBtn}`}
                            onClick={() => handleEditQuickemessage(quickemessage)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.deleteBtn}`}
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingQuickemessage(quickemessage);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={3} />}
              </TableBody>
            </Table>

            {!loading && quickemessages.length === 0 && (
              <Box className={classes.emptyState}>
                <Box className={classes.emptyIcon}>
                  <FlashOnIcon style={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Nenhuma resposta rápida encontrada
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Crie atalhos para agilizar seu atendimento
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default QuickMessages;
